<template>
  <div class="custom-ui-table-wrapper" :class="{ 'is-loading': loading }">
    <ui-loader v-if="loading" :type="3" />
    <table class="table">
      <thead v-if="columns.length > 0">
        <tr>
          <th v-if="orderRows" class="draggable-column sticky"></th>
          <th v-if="isSelectable" class="select-column sticky">
            <ui-checkbox
              v-if="multiSelectVisibility"
              v-model="selectedRows"
              :value="rowData"
              :is-all="true"
            />
          </th>
          <th
            v-for="(column, index) in columns"
            :key="index"
            :class="isSticky && Number(stickyIndex) == index ? 'sticky' : ''"
            @click="sortCol(column)"
          >
            <div class="column-head">
              <span>{{ column.label }}</span>
              <span v-if="isSortable">
                <i
                  v-show="sortColumn === column.name"
                  class="fa-solid"
                  :class="orderByAsc ? 'fa-caret-down' : 'fa-caret-up'"
                />
              </span>
            </div>
          </th>
          <th v-if="deletable" class="delete-column sticky"></th>
        </tr>
      </thead>
      <vue-draggable-next
        v-if="rowData.length > 0"
        :list="rowData"
        :disabled="!orderRows"
        tag="tbody"
        handle=".draggable-icon"
        @change="setOrder"
      >
        <tr v-for="(item, index) in rowData" :key="index">
          <td v-if="orderRows" class="draggable-column sticky">
            <i class="fa-solid fa-arrow-up-arrow-down fa-xs draggable-icon" />
          </td>
          <td v-if="isSelectable" class="select-column sticky">
            <ui-checkbox
              v-if="item.isRowSelectable !== false"
              :id="'checkbox' + index"
              v-model="selectedRows"
              :value="item"
              name="checkbox"
            />
          </td>

          <td
            v-for="(column, indexColumn) in columns"
            :key="indexColumn"
            :class="isSticky && Number(stickyIndex) == indexColumn ? 'sticky' : ''"
          >
            <div v-if="column.type === 'text'" v-text="item[column.name]" />

            <template v-if="column.type == 'long-text'">
              <div
                v-if="column.format"
                class="long-text"
                v-html="column.format(item[column.name], item)"
              />
              <div v-else class="long-text" v-text="item[column.name]" />
            </template>
            <div
              v-if="column.type === 'date'"
              v-text="
                DateTimeParser(item[column.name], column.dateFormat ? column.dateFormat : 'L')
              "
            />
            <div v-if="column.type === 'number'" v-text="item[column.name]" />
            <div v-if="column.type === 'color'" class="color-column">
              <span
                v-if="item[column.name] !== null"
                :style="{ backgroundColor: item[column.name] }"
                class="color-item"
              />

              <span v-else v-text="t('admin_global_notselected')" />
            </div>
            <div v-if="column.type === 'bool'" class="bool-column">
              <div :class="item[column.name] ? 'active' : 'passive'" class="dot" />
              <div>
                {{ item[column.name] ? t('admin_global_active') : t('admin_global_passive') }}
              </div>
            </div>
            <div v-if="column.type === 'chips'" class="chips-column">
              <span
                v-for="(chip, chipIndex) in item[column.name]"
                :key="chipIndex"
                class="chip"
                :class="{ highlighted: chip.highlighted }"
              >
                <i v-if="chip.highlighted" class="fa-solid fa-check" />
                {{ chip.label }}
              </span>
            </div>
            <div
              v-if="column.type === 'html'"
              class="post-column"
              v-html="column.format ? column.format(item[column.name], item) : item[column.name]"
            />
            <div
              v-if="column.type === 'format'"
              class="format-column"
              v-text="column.format(item[column.name], item)"
            />
            <div v-if="column.type === 'image'" class="image-column">
              <span
                v-if="item[column.name] !== null"
                class="image-item"
                @mouseenter="showImagePreview($event, getImageSrc(column, item))"
                @mouseleave="hideImagePreview"
              >
                <img
                  :src="getImageSrc(column, item)"
                  :style="{ width: column.imageWidth }"
                  alt=""
                />
              </span>
              <span v-else class="image-item">
                <i class="fa-regular fa-image-slash" />
              </span>
            </div>
            <div v-if="column.type === 'link'" class="link-column">
              <span v-if="item[column.name]" class="has-link">
                <a :href="item[column.name]" target="_blank" v-text="t('admin_global_openlink')" />
                <span class="copy-clipboard">
                  <i class="fa-solid fa-copy" @click="copyClipboard(item[column.name])" />
                </span>
              </span>
              <span v-else v-text="t('admin_global_linknotfound')" />
            </div>
            <div v-if="column.type === 'action'" class="action-column">
              <ui-switch
                :disabled="item.isActionDisabled"
                v-model="item[column.name]"
                @change="emit('action', item)"
              />
            </div>
          </td>
          <td v-if="deletable" class="delete-column sticky">
            <span @click="emit('delete', index)">
              <i class="fa-solid fa-times-circle" />
            </span>
          </td>
        </tr>
      </vue-draggable-next>
      <tbody v-else>
        <tr>
          <td colspan="100%" class="no-data">
            {{ t(emptyTableData) }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- Teleport: image preview rendered at body level to escape overflow -->
  <Teleport to="body">
    <div
      v-if="imagePreview.visible"
      class="ui-table-image-preview"
      :style="{
        top: imagePreview.y + 'px',
        left: imagePreview.x + 'px',
      }"
    >
      <img :src="imagePreview.src" alt="" />
    </div>
  </Teleport>
</template>

<script setup lang="ts">
  import type { IUITable } from '@/types/Components/UIKit'
  import { DateTimeParser, Toast, UseI18n } from '@/utils-ts'
  import { computed, onMounted, reactive, ref, watch } from 'vue'
  import { VueDraggableNext } from 'vue-draggable-next'

  const {
    columns = [],
    rows = [],
    sortable = false,
    selectable = false,
    stickyIndex = null,
    selectMultiple = false,
    asyncSortable = false,
    emptyTableData = 'admin_global_noresults',
    deletable = false,
    orderRows = false,
    orderOnlyMoved = false,
    loading = false,
  } = defineProps<IUITable>()
  const emit = defineEmits(['select', 'sort', 'setOrder', 'action', 'delete'])

  const { t } = UseI18n()

  const orderByAsc = ref<boolean>(false)
  const sortColumn = ref<string>('')
  const selectedRows = ref<any[] | null>(selectMultiple ? [] : null)
  const rowData = ref<any[]>([])

  const isSortable = computed(() => sortable || asyncSortable)

  // rows prop değiştiğinde rowData'yı güncelle
  watch(
    () => rows,
    newRows => {
      const list = JSON.parse(JSON.stringify(newRows))
      if (!list) {
        rowData.value = []
        return
      }
      if (sortable && sortColumn.value !== '') {
        rowData.value = list.sort((a: any, b: any) => {
          const aValue = a[sortColumn.value]
          const bValue = b[sortColumn.value]
          if (!isNaN(aValue) && !isNaN(bValue)) {
            if (orderByAsc.value) {
              return aValue - bValue
            } else {
              return bValue - aValue
            }
          }
          if (orderByAsc.value) {
            return String(aValue).localeCompare(String(bValue))
          } else {
            return String(bValue).localeCompare(String(aValue))
          }
        })
      } else {
        rowData.value = list
      }
    },
    { immediate: true, deep: true },
  )

  const multiSelectVisibility = computed(() => {
    return (
      selectMultiple &&
      rowData.value.length > 0 &&
      rowData.value.every(item => item.isRowSelectable !== false)
    )
  })

  const isSelectable = computed(() => {
    return selectable
  })

  const isSticky = computed(() => {
    const index = Number(stickyIndex)
    return index !== null && index >= 0 && index < columns.length
  })

  const imagePreview = reactive({
    visible: false,
    src: '',
    x: 0,
    y: 0,
  })

  watch(
    () => selectedRows.value,
    val => {
      if (!val) {
        selectedRows.value = selectMultiple ? [] : null
      }
      emit('select', selectedRows.value)
    },
  )

  watch(
    () => selectedRows.value?.length,
    () => {
      emit('select', selectedRows.value)
    },
  )

  watch(
    () => rows.length,
    () => {
      selectedRows.value = selectMultiple ? [] : null
    },
  )
  watch(
    () => rows,
    () => {
      selectedRows.value = selectMultiple ? [] : null
    },
  )

  onMounted(() => {
    const defaultSort = columns.find(column => column.defaultSort)
    if (defaultSort) {
      sortCol(defaultSort)
      if (defaultSort.sortType === 'desc') {
        orderByAsc.value = false
      } else {
        orderByAsc.value = true
      }
    }
  })

  // format lets the caller cache-bust an image src (e.g. by updatedAt) so an
  // in-place file change is reflected without the URL string itself changing.
  const getImageSrc = (column: any, item: any): string => {
    const value = item[column.name]
    return column.format ? column.format(value, item) : value
  }

  const showImagePreview = (event: MouseEvent, src: string): void => {
    const target = event.currentTarget as HTMLElement
    const rect = target.getBoundingClientRect()
    imagePreview.src = src
    imagePreview.x = rect.left + rect.width / 2
    imagePreview.y = rect.top - 8
    imagePreview.visible = true
  }

  const hideImagePreview = (): void => {
    imagePreview.visible = false
  }

  const sortCol = (column: any): void => {
    if (sortable && !asyncSortable) {
      if (sortable && column.label !== null) {
        sortColumn.value = column.name
        orderByAsc.value = !orderByAsc.value
        rowData.value = [...rowData.value].sort((a: any, b: any) => {
          const aValue = a[sortColumn.value]
          const bValue = b[sortColumn.value]
          if (!isNaN(aValue) && !isNaN(bValue)) {
            return orderByAsc.value ? aValue - bValue : bValue - aValue
          }
          return orderByAsc.value
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue))
        })
      }
    } else if (!sortable && asyncSortable) {
      sortColumn.value = column.name
      orderByAsc.value = !orderByAsc.value
      const sort = {
        column: column.name,
        asc: orderByAsc.value,
      }
      emit('sort', sort)
    }
  }

  const copyClipboard = (link: string): void => {
    window.navigator.clipboard.writeText(link)
    Toast({
      message: t('admin_copied'),
      type: 'success',
    })
  }

  const setOrder = (event: any): void => {
    if (event.moved) {
      if (orderOnlyMoved) {
        emit('setOrder', event)
      } else {
        emit('setOrder', rowData.value)
      }
    }
  }

  defineExpose({
    clearSelection: () => {
      selectedRows.value = selectMultiple ? [] : null
    },
  })
</script>
<style lang="scss">
  .ui-table-image-preview {
    position: fixed;
    z-index: 9999;
    transform: translate(-50%, -100%);
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
    padding: 4px;
    pointer-events: none;

    img {
      max-width: 220px;
      max-height: 220px;
      display: block;
      border-radius: 6px;
      object-fit: contain;
    }
  }
</style>
<style lang="scss" scoped>
  .custom-ui-table-wrapper {
    width: 100%;
    overflow: auto;
    border-radius: 5px;
    .table-actions {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 1rem;
      button {
        margin-left: 0.5rem;
      }
    }

    .sticky {
      position: sticky;
      background-color: inherit;
      left: 0;
      z-index: 1;
    }
    .long-text {
      max-width: 200px;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      word-wrap: break-word;
    }
    .link-column {
      .has-link {
        display: flex;
        align-items: center;
        justify-content: space-between;
        i {
          color: $brandColor;
          cursor: pointer;
        }
        a {
          color: $brandColor;
          text-decoration: underline;
        }
      }
    }
    .chips-column {
      display: flex;
      flex-wrap: wrap;
      gap: 0.25rem;
      .chip {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        border: 1px solid $info;
        color: $info;
        border-radius: 1rem;
        padding: 0.125rem 0.5rem;
        font-size: 0.825rem;
        white-space: nowrap;
        &.highlighted {
          background-color: $info;
          color: $white;
        }
      }
    }
    .bool-column {
      display: flex;
      align-items: center;
      .dot {
        width: 15px;
        height: 15px;
        border-radius: 50%;
        margin-right: 0.5rem;
        &.active {
          background: rgb(56, 230, 56);
        }
        &.passive {
          background: rgb(230, 30, 30);
        }
      }
    }
    .image-column {
      .image-item {
        display: inline-flex;
        width: 40px;
        height: 40px;
        border-radius: 5px;
        cursor: pointer;
        background-color: #fbfbfb;
        @include flexible($align: center, $justify: center);

        i {
          color: #ddd;
        }

        img {
          height: 40px;
          width: 40px;
          border-radius: 5px;
          object-fit: cover;
        }
      }
    }
    .action-column {
      cursor: pointer;
      &:hover {
        text-decoration: underline;
      }
    }
    .order-column {
      @include flexible($align: center, $justify: space-around);

      .order-caret {
        color: $brandColor;
        cursor: pointer;
      }
    }

    table {
      border-collapse: separate;
      border-radius: 5px;
      overflow: hidden;
      overflow-x: auto;
      position: relative;
      border-spacing: 0;
      width: 100%;
      border: 1px solid $border-color;
    }
    th {
      background-color: #f1f0f0 !important;
      text-align: left;
      padding: 0.5rem;
      border-bottom: 1px solid $border-color;
    }
    .column-head {
      display: flex;
      justify-content: space-between;
      align-items: center;
      color: $primary-text-color;
    }
    td {
      padding: 0.5rem;
    }
    tbody {
      tr {
        &:last-child {
          border-bottom: 1px solid $border-color;
        }
        &:nth-child(odd) {
          background-color: $white;
        }
        &:nth-child(even) {
          background-color: #f1f0f0;
        }
      }
    }
    .color-column {
      .color-item {
        width: 20px;
        height: 20px;
        border-radius: 5px;
        display: flex;
        border: 1px solid #000;
      }
    }
    .select-column,
    .draggable-column {
      min-width: 50px !important;
      text-align: center;
      max-width: 50px !important;
      width: 50px !important;
    }

    .draggable-icon {
      cursor: grab;
      color: $brandColor;
    }

    .delete-column {
      text-align: center;
      max-width: 50px !important;
      width: 50px !important;
      min-width: 50px;
      color: $danger;
      cursor: pointer;
    }
    th,
    td {
      min-width: 100px;
      &:not(:last-child) {
        border-right: 1px solid #dedede;
      }
    }
    .no-data {
      text-align: center;
      padding: 1rem;
    }
  }
</style>
